import React, { useEffect, useMemo } from 'react';

import { usePersistedApi } from 'hooks';
import entities from 'reduxStore/entityKeys';
import Api from 'api';

import styles from '../DentistNavbar/DentistNavbar.module.scss';

/**
 * T2-6189: Shared header for the DO slide-out side menu. Replaces the old
 * "avatar + user first/last name" block with the OFFICE identity:
 *   - multiple active Locations -> "{N} Locations"
 *   - exactly one active Location -> that Location's practiceName
 * The avatar/initial circle is removed (the space is reserved for a future
 * "upload office picture" feature). Extracted into one component so the two DO
 * menus (DentistSideMenu + DentistNavbar) can't drift apart.
 *
 * Count source: the same `dentistBillingLocations` Redux entity that
 * LocationsListPage uses (so add/remove flows keep it in sync), with the
 * login-time session locations as a fallback before that entity is populated.
 * Active = not removed (Location.removed !== 1).
 */

interface MenuLocation {
  practiceName?: string | null;
  removed?: number | null;
}

// Match the legacy capitalize() treatment: title-case names stored fully
// lowercase (e.g. "maple dental" -> "Maple Dental") while leaving already-cased
// names and acronyms ("TLC Dental") untouched.
const formatPracticeName = (name: string): string => {
  if (!name) return '';
  if (name === name.toLowerCase()) {
    return name.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return name;
};

const DentistSideMenuHeader: React.FC = () => {
  const [locations, , , getLocations] = usePersistedApi<{}, MenuLocation[]>({
    apiMethod: Api.getLocations,
    entityName: entities.dentistBillingLocations,
    initialValue: [],
  });

  useEffect(() => {
    getLocations({});
  }, [getLocations]);

  const headerText = useMemo(() => {
    const sessionLocations: MenuLocation[] | undefined =
      (window as any)?.TempStars?.User?.getCurrentUser?.()?.locations;

    const source: MenuLocation[] = Array.isArray(locations) && locations.length > 0
      ? locations
      : (Array.isArray(sessionLocations) ? sessionLocations : []);

    const active = source.filter((l) => l?.removed !== 1);

    if (active.length === 1) {
      return formatPracticeName(active[0]?.practiceName || '');
    }
    // Multiple (or the not-expected 0) active locations -> "{N} Locations".
    return `${active.length} Locations`;
  }, [locations]);

  return (
    <div className={styles.sideMenuHeader}>
      <div className={styles.officeIdentity} title={headerText} aria-label={headerText}>
        {headerText}
      </div>
    </div>
  );
};

export default DentistSideMenuHeader;
